import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import {
  CATEGORY_PREFIX,
  RecommendedProductFamily,
  RecommendedProductFamilyFacets,
  SortValues,
  Technique,
} from '@omniafishing/core';
import { Checkbox, Col, Dropdown, Menu, Modal, Pagination, Row, Tree } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { DataNode } from 'antd/lib/tree';
import { MenuInfo } from 'rc-menu/lib/interface';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cssStyles from '../../../components/category_products_grid/category_products_grid.less';
import { CollapsiblePanel } from '../../../components/collapsible_panel/collapsible_panel';
import { ContentWrapper } from '../../../components/content_wrapper/content_wrapper';
import { FilterTag } from '../../../components/filter_tag/filter_tag';
import Loading from '../../../components/loading/loading';
import { OmniaButton } from '../../../components/omnia_button/omnia_button';
import { OmniaSwitch } from '../../../components/omnia_switch/omnia_switch';
import { PRODUCTS_NAV_HEIGHT_APPROX } from '../../../components/products_nav/products_nav';
import { NearYouBadge } from '../../../components/product_card/near_you_badge';
import { ProductFamilyCard } from '../../../components/product_family_card/product_family_card';
import { SortResults } from '../../../components/sort_results/sort_results';
import { isPending, LoadingState } from '../../../constants/loading_state';
import { useCart } from '../../../hooks/use_cart';
import { useCatalogPage } from '../../../hooks/use_catalog_page';
import { useListAttributionUuid } from '../../../hooks/use_list_attribution_uuid';
import { useQueryString } from '../../../hooks/use_query_string';
import { useUserProfile } from '../../../hooks/use_user_profile';
import { addOrRemoveFromArray } from '../../../lib/add_or_remove_from_array';
import { RecommendationsFacets, RECOMMENDATIONS_PAGE_SIZE_DEFAULT } from '../../../lib/api';
import { isProductInStock } from '../../../lib/products';
import { OmniaQueryParams } from '../../../lib/query_string';
import { scrollToElementTop } from '../../../lib/scroll';
import { WebAnalytics } from '../../../lib/web_analytics';
import { RecommendationsActions } from '../../../redux/recommendations';
import { getBrandsNotHidden, getSpecies, getTechniques } from '../../../redux/reference_data';
import { getPath } from '../../../redux/router';
import { UserPreferencesActions } from '../../../redux/user_preferences';
import { getIsMobile } from '../../../redux/window';
import { RoutePaths } from '../../../routes';
import common from '../../../styles/common.less';

interface TechniquePageQueryParams {
  [OmniaQueryParams.pg]: number;
  [OmniaQueryParams.in_stock]: boolean;
  [OmniaQueryParams.brands]: string[];
  [OmniaQueryParams.species]: string[];
  [OmniaQueryParams.subcategories]: string[];
}

export interface TechniqueProductsGridProps {
  defaultSortOrder: SortValues;
  loadingState: LoadingState;
  recommendation_id: string;
  recommendedFamilies: RecommendedProductFamily[];
  recommendedFamiliesTotalResults: number;
  recommendedFamiliesFacets: RecommendedProductFamilyFacets;
  technique: Technique;
}

export const TechniqueProductsGrid = (props: TechniqueProductsGridProps) => {
  const {
    defaultSortOrder,
    loadingState,
    recommendation_id,
    recommendedFamilies,
    recommendedFamiliesTotalResults,
    recommendedFamiliesFacets,
    technique,
  } = props;

  const dispatch = useDispatch();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const divRef = useRef<HTMLDivElement>();

  const brands = useSelector(getBrandsNotHidden);
  const { cartDiscountCodes } = useCart();
  const coupon = cartDiscountCodes[0]?.code;
  const isMobile = useSelector(getIsMobile);
  const path = useSelector(getPath);
  const species = useSelector(getSpecies);
  const techniques = useSelector(getTechniques);
  const { userProfile } = useUserProfile();

  const { replaceQueryString, pushQueryString, getCurrentQuery } = useQueryString();
  const currentQuery = getCurrentQuery<TechniquePageQueryParams>();
  const currentPage = Number(currentQuery.pg || 1);
  const inStockOnly = currentQuery.in_stock || false;
  const selectedBrands = currentQuery.brands || [];
  const selectedSpecies = currentQuery.species || [];
  const selectedSubcategories = currentQuery.subcategories || [];
  const userSpecies = useMemo(() => {
    return selectedSpecies.length ? selectedSpecies : userProfile?.species.map((s) => s.name) || [];
  }, [userProfile, selectedSpecies]);
  const { productFamiliesFishingReports, brandsWithCount, productSubcategoriesInCategories } =
    useCatalogPage({
      recommendedFamilies,
      recommendedFamiliesFacets,
      selectedSpecies: userSpecies,
    });

  useEffect(() => {
    return () => {
      dispatch(RecommendationsActions.RECOMMENDATIONS_RESET());
    };
  }, []);

  const list_uuid = useListAttributionUuid();
  useEffect(() => {
    WebAnalytics.productFamilyListViewed({
      coupon,
      productFamilies: recommendedFamilies.map((rf) => rf.product_family),
      list_id: `${path}:store`,
      recommendation_id,
      list_uuid,
    });
  }, [recommendedFamilies]);

  const scrollTop = () => {
    scrollToElementTop(divRef.current, PRODUCTS_NAV_HEIGHT_APPROX);
  };

  const onBrandChange = (event: CheckboxChangeEvent) => {
    const value = (event.target as any)['data-value'];
    const newBrands = addOrRemoveFromArray(selectedBrands, value);
    pushQueryString({
      [OmniaQueryParams.brands]: newBrands,
      [OmniaQueryParams.pg]: 1,
    });
    scrollTop();

    WebAnalytics.productCategoryFiltered({
      category_name: null,
      subcategory_name: null,
      filter_type: 'brand',
      filter_value: newBrands.sort().join(','),
      sort_type: null,
    });
  };

  const onSubcategoryChange = (checkedKeys: string[]) => {
    pushQueryString({
      subcategories: checkedKeys.filter((key) => !key.includes(CATEGORY_PREFIX)),
      pg: '1',
    });
    scrollTop();
  };

  const getSubcategoriesFilters = () => {
    const treeData: DataNode[] = productSubcategoriesInCategories.map((cat) => {
      const categoryCount = cat.subcategories.reduce((sum, subcat) => sum + subcat.count, 0);

      return {
        title: `${cat.display_name} (${categoryCount})`,
        key: `${CATEGORY_PREFIX}${cat.name}`,
        selectable: false,
        active: true,
        children: cat.subcategories.map((subcategory) => {
          return {
            title: `${subcategory.display_name} (${subcategory.count})`,
            key: subcategory.name,
            selectable: false,
            active: true,
          };
        }),
      };
    });

    return (
      <CollapsiblePanel title="Categories" defaultOpenDesktop={true}>
        <Tree
          checkable
          onCheck={onSubcategoryChange}
          checkedKeys={selectedSubcategories}
          className={cssStyles.tree}
          switcherIcon={<PlusOutlined />}
          treeData={treeData}
        />
      </CollapsiblePanel>
    );
  };

  const clearFilters = () => {
    replaceQueryString({
      [OmniaQueryParams.brands]: null,
      [OmniaQueryParams.species]: null,
      [OmniaQueryParams.styles]: null,
      [RecommendationsFacets.pg]: '1',
    });
  };

  const onFilterTagRemove = (facet: keyof TechniquePageQueryParams, value: string) => () => {
    const currentQueryValues = currentQuery[facet] || [];

    const newValue = Array.isArray(currentQueryValues)
      ? addOrRemoveFromArray<any>(currentQueryValues, value)
      : null;

    replaceQueryString({
      [facet]: newValue,
    });
  };

  const getFilterTags = () => {
    const selectedBrandsFull = brands.filter((b) => selectedBrands.includes(b.name));
    const selectedSpeciesFull = species.filter((s) => selectedSpecies.includes(s.name));

    const hasFilters = selectedBrandsFull.length > 0 || selectedSpeciesFull.length > 0;

    return (
      <div className={cssStyles.filterTags}>
        {selectedSpeciesFull.map((s) => (
          <FilterTag key={s.name} onClose={onFilterTagRemove(OmniaQueryParams.species, s.name)}>
            {s.display_name}
          </FilterTag>
        ))}
        {selectedBrandsFull.map((b) => (
          <FilterTag key={b.name} onClose={onFilterTagRemove(OmniaQueryParams.brands, b.name)}>
            {b.display_name}
          </FilterTag>
        ))}
        {hasFilters && (
          <span className={cssStyles.clearFilters} onClick={clearFilters}>
            Clear All
          </span>
        )}
      </div>
    );
  };

  const onSpeciesAdd = (e: MenuInfo) => {
    const newSelectedSpecies = addOrRemoveFromArray([...selectedSpecies], e.key);
    pushQueryString({
      [RecommendationsFacets.species]: newSelectedSpecies,
      [RecommendationsFacets.styles]: [],
      [RecommendationsFacets.pg]: 1,
    });
    WebAnalytics.productCategoryFiltered({
      category_name: null,
      subcategory_name: null,
      filter_type: 'species',
      filter_value: newSelectedSpecies.sort().join(','),
      sort_type: null,
    });
    dispatch(UserPreferencesActions.USER_PREFERENCES_SPECIES_SAVE(e.key));
  };

  const getFilters = () => {
    const techniqueContainsSpeciesName = species.some(
      (s) =>
        technique.name.includes(s.name) ||
        technique.display_name.toLowerCase().includes(s.display_name.toLowerCase())
    );

    const speciesMenu = (
      <Menu onClick={onSpeciesAdd} className={cssStyles.speciesMenu}>
        {species
          .filter((specie) => !selectedSpecies.includes(specie.name))
          .map((specie) => (
            <Menu.Item key={specie.name}>{specie.display_name}</Menu.Item>
          ))}
      </Menu>
    );

    const panels = (
      <>
        <Col xs={24}>
          <section className={cssStyles.topFilters}>
            <div className={cssStyles.inStockFilter}>
              <OmniaSwitch
                checked={inStockOnly}
                onChange={(checked) => {
                  replaceQueryString({
                    [OmniaQueryParams.in_stock]: checked ? 'true' : null,
                  });
                }}
                switchSide="left"
              >
                Show In Stock only
              </OmniaSwitch>
            </div>
            {!techniqueContainsSpeciesName && (
              <div>
                <Dropdown overlay={speciesMenu} trigger={['click']} placement="bottom">
                  <span className={cssStyles.filterText}>
                    Choose a Species <PlusOutlined />
                  </span>
                </Dropdown>
              </div>
            )}
            {!isMobile && <SortResults setQueryString defaultSortValue={defaultSortOrder} />}
          </section>
        </Col>

        <Col xs={24} sm={6}>
          {getSubcategoriesFilters()}

          <CollapsiblePanel title="Brands" defaultOpenDesktop={true}>
            {brandsWithCount.map((brand) => (
              <Checkbox
                onChange={onBrandChange}
                data-value={brand.name}
                data-test={`facet_brand:${brand.name}`}
                key={brand.name}
                className={common.facetCheckbox}
                checked={selectedBrands.includes(brand.name)}
                disabled={brand.count === 0}
              >
                {brand.display_name} {brand.count > 0 && `(${brand.count})`}
              </Checkbox>
            ))}
          </CollapsiblePanel>

          <CollapsiblePanel title="More Techniques" defaultOpenDesktop={true}>
            <ul className={common.facetList}>
              {techniques.map((t) => (
                <li key={t.name}>
                  <NavLink
                    to={`${RoutePaths.TECHNIQUES}/${t.url_path}`}
                    className={common.facetLink}
                    activeClassName={common.facetLink__active}
                  >
                    {t.display_name}
                  </NavLink>
                </li>
              ))}
            </ul>
          </CollapsiblePanel>
        </Col>
      </>
    );

    if (isMobile) {
      return (
        <>
          <Col xs={24}>
            <section className={cssStyles.topFiltersMobile}>
              <OmniaButton
                kind="tertiary"
                size="md"
                onClick={() => {
                  setFiltersOpen(true);
                }}
              >
                <FilterOutlined />
                Filters
              </OmniaButton>

              <SortResults setQueryString defaultSortValue={defaultSortOrder} />
            </section>
          </Col>
          <Modal
            open={filtersOpen}
            onCancel={() => {
              setFiltersOpen(false);
            }}
            footer={
              <OmniaButton
                onClick={() => {
                  setFiltersOpen(false);
                }}
                kind="secondary"
                size="md"
              >
                Done
              </OmniaButton>
            }
          >
            {panels}
          </Modal>
        </>
      );
    }

    return panels;
  };

  if (!technique) {
    return null;
  }

  const recommendedProducts = inStockOnly
    ? recommendedFamilies
        .map((recommendedFamily) => {
          return {
            ...recommendedFamily,
            products: recommendedFamily.products.filter(isProductInStock),
          };
        })
        .filter((recommendedFamily) => recommendedFamily.products.length > 0)
    : recommendedFamilies;

  return (
    <section>
      <ContentWrapper mobileNoPadding>
        <Row gutter={0}>
          {getFilters()}

          <Col xs={24} sm={18}>
            <div ref={divRef}>
              <div className={cssStyles.products}>
                {getFilterTags()}

                {isPending(loadingState) ? (
                  <Loading text="Loading Products" />
                ) : (
                  <>
                    {recommendedProducts.length > 0 ? (
                      <ol className={cssStyles.productList}>
                        {recommendedProducts.map((recommendedFamily, i) => {
                          const reportedOnLake =
                            productFamiliesFishingReports[recommendedFamily.product_family.handle]
                              ?.waterbody;
                          return (
                            <li
                              className={cssStyles.productCardWrapper}
                              key={recommendedFamily.product_family.handle}
                            >
                              <ProductFamilyCard
                                recommendedFamily={recommendedFamily}
                                position={i}
                                recommendation_id={recommendation_id}
                                className={cssStyles.productCard}
                                reportedOnLake={reportedOnLake}
                                icon={reportedOnLake != null && <NearYouBadge />}
                                showStateTotal
                                list_uuid={list_uuid}
                              />
                            </li>
                          );
                        })}
                      </ol>
                    ) : (
                      <>
                        <p>No products found.</p>
                      </>
                    )}
                    <div className={cssStyles.pagination}>
                      <Pagination
                        current={currentPage}
                        pageSize={RECOMMENDATIONS_PAGE_SIZE_DEFAULT}
                        total={recommendedFamiliesTotalResults}
                        showSizeChanger={false}
                        hideOnSinglePage
                        onChange={(page) => {
                          scrollTop();
                          pushQueryString({
                            [OmniaQueryParams.pg]: page,
                          });
                        }}
                        itemRender={(current, type, originalElement) => {
                          if (type === 'prev') {
                            return <a rel="nofollow">Previous</a>;
                          }
                          if (type === 'next') {
                            return <a rel="nofollow">Next</a>;
                          }
                          return originalElement;
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </ContentWrapper>
    </section>
  );
};
